import type {
  CommittedRow,
  InitializeAddDepartmentsRequest,
  InitializeAddDepartmentsResponse,
  InitializeAddProcessesRequest,
  InitializeAddProcessesResponse,
  InitializeAddSystemsRequest,
  InitializeAddSystemsResponse,
  InitializeAddUsersRequest,
  InitializeAddUsersResponse
} from '@bliks-io/contracts';
import type { ParsedSystemRow } from '@/lib/excel';
import type { InitializeProcessDraft } from '@/components/bulk-upload';
import type { DraftRowShape, RowValidator } from '@/lib/draft-rows/draftRow';
import type { AnchorImportPayload } from './utils/buildAnchorImportPayload';
import {
  departmentCrossRowChecks,
  departmentDuplicateKeys,
  processDuplicateKeys,
  systemDuplicateKeys,
  userDuplicateKeys,
  validateDepartmentRow,
  validateUserRow
} from './utils/draftRowValidation';
import type { WizardFlushBaselines } from './utils/flushBaselines';
import { protectedUserIds } from './utils/protectedRows';
import type { CrossRowCheck, DuplicateCheck } from '@/lib/draft-rows/markDuplicateRows';
import type { ParsedDepartment, ParsedUser } from './utils/excelParsers';
import type { WizardValidationContext } from './useWizardValidationContext';
import type { WizardDrafts } from './useWizardSubmit';
import type { WizardProgress } from './wizardSteps';

export interface WizardSeeds {
  departmentSeeds: ParsedDepartment[];
  userSeeds: ParsedUser[];
  systemSeeds: ParsedSystemRow[];
  processSeeds: InitializeProcessDraft[];
}

/**
 * Everything one structural-anchor step knows about itself. The dirty check,
 * the silent autosave flush, and the authoritative Weiter submit all consume
 * the SAME spec — the design invariant is that their verdicts (and payloads)
 * can never drift apart, which hand-copied per-step tuples cannot guarantee.
 */
export interface AnchorStepSpec<
  TData extends object,
  TRow extends DraftRowShape<TData>,
  TRequest,
  TResponse = unknown
> {
  baselineKey: keyof Pick<WizardFlushBaselines, 'departments' | 'users' | 'systems' | 'processes'>;
  /**
   * Optional steps navigate on Weiter over an empty never-imported draft
   * (identical to Überspringen); the two structural anchors error instead.
   */
  optionalStep?: true;
  selectDrafts: (drafts: WizardDrafts) => TRow[];
  selectSeeds: (seeds: WizardSeeds) => TRow[];
  selectBaseline: (baselines: WizardFlushBaselines) => TRow[] | undefined;
  isDone: (progress: WizardProgress) => boolean;
  /**
   * Built per pass because some steps' row validators are member-aware
   * (owner emails must resolve to an internal member) — the validation
   * context carries the memoized member-aware instances.
   */
  buildValidateRow: (validation: WizardValidationContext) => RowValidator<TData>;
  duplicateChecks: DuplicateCheck<TData>[];
  buildCrossRowChecks: (
    validation: WizardValidationContext,
    progress: WizardProgress
  ) => CrossRowCheck<TData>[];
  /**
   * Ids of protected rows (members the add-users pipeline may never touch):
   * partitioned out before classifyAnchorDrafts and committed via the
   * placement PATCH instead. Absent on steps without protected rows.
   */
  selectProtectedIds?: (
    validation: WizardValidationContext,
    progress: WizardProgress
  ) => ReadonlySet<string>;
  /** False blocks a silent flush quietly; Weiter surfaces its own toast. */
  canSubmit: (validation: WizardValidationContext) => boolean;
  /** The ONE wire mapping both the silent and the loud path send through. */
  buildRequest: (
    payload: AnchorImportPayload<TData>,
    validation: WizardValidationContext
  ) => TRequest;
  /** The response's per-row results, aligned with the request's create rows. */
  selectRowResults: (response: TResponse) => CommittedRow[];
}

export const departmentsStepSpec: AnchorStepSpec<
  ParsedDepartment['data'],
  ParsedDepartment,
  InitializeAddDepartmentsRequest,
  InitializeAddDepartmentsResponse
> = {
  baselineKey: 'departments',
  selectDrafts: (drafts) => drafts.departments,
  selectSeeds: (seeds) => seeds.departmentSeeds,
  selectBaseline: (baselines) => baselines.departments,
  isDone: (progress) => progress.departmentsDone,
  buildValidateRow: () => validateDepartmentRow,
  duplicateChecks: departmentDuplicateKeys,
  buildCrossRowChecks: () => departmentCrossRowChecks,
  canSubmit: () => true,
  buildRequest: ({ rows, updates, removals }) => ({
    departments: rows,
    ...(updates && { updates }),
    ...(removals && { removals })
  }),
  selectRowResults: (response) => response.departments
};

export const usersStepSpec: AnchorStepSpec<
  ParsedUser['data'],
  ParsedUser,
  InitializeAddUsersRequest,
  InitializeAddUsersResponse
> = {
  baselineKey: 'users',
  selectDrafts: (drafts) => drafts.users,
  selectSeeds: (seeds) => seeds.userSeeds,
  selectBaseline: (baselines) => baselines.users,
  isDone: (progress) => progress.usersDone,
  buildValidateRow: () => validateUserRow,
  duplicateChecks: userDuplicateKeys,
  buildCrossRowChecks: (validation) => validation.userCrossRowChecks,
  selectProtectedIds: (validation, progress) =>
    protectedUserIds(validation.orgMembers ?? [], progress.usersDone),
  // Without the member lists the protected-row partition and the
  // unrepresented-member check silently no-op — protected members would be
  // sent through the add-users pipeline.
  canSubmit: (validation) => validation.membersLoaded,
  buildRequest: ({ rows, updates, removals }, validation) => ({
    users: rows.map(validation.toWireDepartment),
    ...(updates && { updates: updates.map(validation.toWireDepartment) }),
    ...(removals && { removals })
  }),
  selectRowResults: (response) => response.users
};

export const systemsStepSpec: AnchorStepSpec<
  ParsedSystemRow['data'],
  ParsedSystemRow,
  InitializeAddSystemsRequest,
  InitializeAddSystemsResponse
> = {
  baselineKey: 'systems',
  optionalStep: true,
  selectDrafts: (drafts) => drafts.systems,
  selectSeeds: (seeds) => seeds.systemSeeds,
  selectBaseline: (baselines) => baselines.systems,
  isDone: (progress) => progress.systemsDone,
  buildValidateRow: (validation) => validation.validateSystemRow,
  duplicateChecks: systemDuplicateKeys,
  buildCrossRowChecks: () => [],
  canSubmit: () => true,
  buildRequest: ({ rows, updates, removals }) => ({
    systems: rows,
    ...(updates && { updates }),
    ...(removals && { removals })
  }),
  selectRowResults: (response) => response.systems
};

export const processesStepSpec: AnchorStepSpec<
  InitializeProcessDraft['data'],
  InitializeProcessDraft,
  InitializeAddProcessesRequest,
  InitializeAddProcessesResponse
> = {
  baselineKey: 'processes',
  optionalStep: true,
  selectDrafts: (drafts) => drafts.processes,
  selectSeeds: (seeds) => seeds.processSeeds,
  selectBaseline: (baselines) => baselines.processes,
  isDone: (progress) => progress.processesDone,
  buildValidateRow: (validation) => validation.validateProcessRow,
  duplicateChecks: processDuplicateKeys,
  buildCrossRowChecks: () => [],
  canSubmit: () => true,
  buildRequest: ({ rows, updates, removals }, validation) => ({
    processes: rows.map(validation.toWireDepartment),
    ...(updates && { updates: updates.map(validation.toWireDepartment) }),
    ...(removals && { removals })
  }),
  selectRowResults: (response) => response.processes
};
